import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Full-screen camera barcode/QR scanner overlay.
 *
 * Props:
 *   onScan(value: string) — called with the decoded text on a successful scan
 *   onClose()             — called when the user dismisses the scanner
 */
const BarcodeScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const hasFiredRef = useRef(false); // prevent double-fire on rapid decode
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(true);
  const regionId = 'barcode-scanner-region';

  const stopScanner = async (scanner) => {
    if (!scanner) return;
    try {
      const state = scanner.getState();
      // Only call stop() when the scanner is actually running or paused
      if (
        state === Html5QrcodeScannerState.SCANNING ||
        state === Html5QrcodeScannerState.PAUSED
      ) {
        await scanner.stop();
      }
    } catch (_) {
      // Ignore — scanner may have already stopped itself after a decode
    }
    try {
      scanner.clear();
    } catch (_) {
      // Ignore cleanup errors
    }
  };

  useEffect(() => {
    const scanner = new Html5Qrcode(regionId);
    scannerRef.current = scanner;

    const config = {
      fps: 10,
      qrbox: { width: 260, height: 160 },
      aspectRatio: 1.7,
      rememberLastUsedCamera: false,
      formatsToSupport: [
        0,  // QR_CODE
        1,  // AZTEC
        2,  // CODABAR
        3,  // CODE_39
        4,  // CODE_93
        5,  // CODE_128
        6,  // DATA_MATRIX
        8,  // EAN_8
        9,  // EAN_13
        11, // ITF
        13, // PDF_417
        15, // UPC_A
        16, // UPC_E
      ],
    };

    scanner
      .start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          // Guard: html5-qrcode may call this multiple times on the same frame
          if (hasFiredRef.current) return;
          hasFiredRef.current = true;

          // The library has already stopped internally after a successful decode.
          // Do NOT call scanner.stop() here — it will throw "not running".
          // Just call onScan directly; cleanup happens in the useEffect return.
          onScan(decodedText);
        },
        () => {
          // Per-frame failure — fires constantly while no code in view; ignore.
        }
      )
      .then(() => setIsStarting(false))
      .catch((err) => {
        const msg = err?.message || '';
        setError(
          msg.toLowerCase().includes('permission')
            ? 'Camera permission denied. Please allow camera access and try again.'
            : 'Could not start camera. Please try again.'
        );
        setIsStarting(false);
      });

    return () => {
      // Cleanup when the component unmounts (scan fired or user closed)
      stopScanner(scannerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80">
        <p className="text-white text-sm font-medium">
          Point camera at barcode or QR code
        </p>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Close scanner"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Scanner viewport */}
      <div className="flex-1 flex flex-col items-center justify-center relative">
        {isStarting && !error && (
          <p className="text-white/70 text-sm absolute top-4">
            Starting camera…
          </p>
        )}

        {error ? (
          <div className="px-6 text-center">
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-black rounded text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <div id={regionId} className="w-full" />
        )}
      </div>

      {!error && (
        <p className="text-white/50 text-xs text-center pb-4">
          Keep the barcode inside the viewfinder
        </p>
      )}
    </div>
  );
};

export default BarcodeScanner;
